// Utility functions for handling Patreon-exclusive chapters

// Simple hash function to generate tokens
// In production, you might want to use a more secure method
function simpleHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

// Secret key for generating tokens (in production, use environment variable)
const SECRET_KEY = 'spirit-falls-patreon-2024';

/**
 * Check if a chapter is patreon-exclusive (not publicly available)
 * Uses the `isPublic` flag set by the update_chapter_visibility.py script
 * @param {Object} chapter - Chapter object with isPublic flag
 * @returns {boolean} - True if chapter is patreon-exclusive
 */
export function isPatreonExclusive(chapter) {
  if (!chapter) {
    return false;
  }
  
  // Use the isPublic flag - chapters without this flag default to public
  return chapter.isPublic === false;
}

/**
 * Generate a unique access token for a patreon-exclusive chapter
 * @param {number} chapterId - Chapter ID
 * @returns {string} - Access token
 */
export function generateChapterToken(chapterId) {
  const tokenString = `${SECRET_KEY}-chapter-${chapterId}`;
  return simpleHash(tokenString);
}

/**
 * Verify if a token is valid for a specific chapter
 * @param {number} chapterId - Chapter ID
 * @param {string} token - Access token to verify
 * @returns {boolean} - True if token is valid
 */
export function verifyChapterToken(chapterId, token) {
  const expectedToken = generateChapterToken(chapterId);
  return token === expectedToken;
}

/**
 * Generate a patreon share link for a chapter
 * @param {number} chapterId - Chapter ID
 * @returns {string} - Full URL with access token
 */
export function generatePatreonLink(chapterId) {
  const token = generateChapterToken(chapterId);
  // In production, use window.location.origin or a config value
  const baseUrl = window.location.origin || 'http://localhost:3000';
  return `${baseUrl}/chapter/${chapterId}/page/1?access=${token}`;
}

/**
 * Filter out patreon-exclusive chapters from a list
 * Uses the `isPublic` flag set by the update_chapter_visibility.py script
 * @param {Array} chapters - Array of chapter objects
 * @returns {Array} - Filtered chapters (only publicly available)
 */
export function filterPublicChapters(chapters) {
  return chapters.filter(chapter => chapter.isPublic !== false);
}

/**
 * Check if user has access to a chapter (either public or has valid token)
 * @param {Object} chapter - Chapter object
 * @param {string|null} accessToken - Access token from URL
 * @returns {boolean} - True if user can access the chapter
 */
export function hasChapterAccess(chapter, accessToken = null) {
  // Public chapters are always accessible
  if (!isPatreonExclusive(chapter)) {
    return true;
  }
  
  // Patreon-exclusive chapters require valid token
  if (accessToken && verifyChapterToken(chapter.id, accessToken)) {
    return true;
  }
  
  return false;
}

